import {
  Box,
  HStack,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useColorModeValue as mode,
  Spacer,
  Switch,
  Text,
  useColorMode,
  useColorModeValue,
} from '@cardboard-ui/react';
import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { t } from '@lingui/macro';
import { useDesktopMenuStyle } from 'apps/TenantApp/theme/components/DesktopMenu';
import {
  AccountExpandIcon,
  MoonIcon,
  PowerOffIcon,
  SettingsWheelIcon,
} from 'components/icons';
import { UserAvatar } from 'components/UserAvatar';
import { UserHandle } from 'components/UserHandle';
import React, { FC } from 'react';
import { graphql, useFragment } from 'utils/graphClient';
import {
  ACCOUNT_SWITCHER_QUERY_PARAM_NAME,
  MY_SETTINGS_QUERY_PARAM_NAME,
} from 'utils/routes';
import { useSession } from 'utils/sessionProvider';
import { useDesktopMenu } from './DesktopMenuContext';
import { AccountMenuSessionInfo$key } from './__generated__/AccountMenuSessionInfo.graphql';
import isDevMode from 'utils/isDevMode';
import { useCardboardDevTools } from 'utils/sessionProvider/provider';
import { useCustomTheme } from 'apps/TenantApp/theme/CustomThemeProvider/useCustomTheme';

interface AccountMenuProps {
  sessionInfo: AccountMenuSessionInfo$key;
}

const AccountMenuSessionInfo = graphql`
  fragment AccountMenuSessionInfo on SessionInfo {
    member {
      id
      ...UserHandle_member
      ...UserAvatar_member
    }
  }
`;

const AccountMenu: FC<AccountMenuProps> = ({ sessionInfo }) => {
  const { member } = useFragment(AccountMenuSessionInfo, sessionInfo),
    { signOutNow } = useSession();
  const { colorMode } = useColorMode();
  const { setColorMode } = useCustomTheme();
  const { accountButton } = useDesktopMenuStyle();
  const { isCollapsed } = useDesktopMenu();
  const devBg = useColorModeValue('orange.200', 'orange.700');
  const { show: showDevTools, isAvailable: isAvailableDevTools } =
    useCardboardDevTools();

  if (!member) {
    return null;
  }

  return (
    <Menu matchWidth placement="top">
      <MenuButton pb={isCollapsed ? 2 : 0}>
        {isCollapsed ? (
          <UserAvatar m={2} member={member} />
        ) : (
          <HStack spacing={0} flexShrink={0} __css={accountButton}>
            <UserHandle
              member={member}
              showAvatar
              nameProps={{ textAlign: 'start', fontSize: 'sm' }}
              showEmail
              emailProps={{ textAlign: 'start', fontSize: 'xs' }}
              margin={2}
              flex={1}
              fontWeight="medium"
              lineHeight="shorter"
            />
            <Box fontSize="lg">
              <AccountExpandIcon />
            </Box>
          </HStack>
        )}
      </MenuButton>
      <MenuList shadow="lg" py="4" color={mode('gray.600', 'gray.200')} px="3">
        <MenuItem
          as={Link}
          icon={<SettingsWheelIcon />}
          to={{ search: `?${MY_SETTINGS_QUERY_PARAM_NAME}=default` }}
        >
          <Text>{t`My settings`}</Text>
        </MenuItem>

        {window.preload_AllowAccountSwitching && (
          <MenuItem
            as={Link}
            icon={<FontAwesomeIcon icon={light('people-arrows')} />}
            to={{ search: `?${ACCOUNT_SWITCHER_QUERY_PARAM_NAME}=1` }}
          >
            <Text>{t`Switch account`}</Text>
          </MenuItem>
        )}

        {isDevMode() && (
          <MenuItem
            icon={<MoonIcon />}
            onClick={() => {
              setColorMode(colorMode === 'light' ? 'dark' : 'light');
            }}
            bg={devBg}
          >
            <HStack>
              <Text>{t`Dark mode`}</Text>
              <Spacer />
              <Switch id="dark-mode" isChecked={colorMode === 'dark'} />
            </HStack>
          </MenuItem>
        )}
        {isAvailableDevTools && (
          <MenuItem
            icon={<FontAwesomeIcon icon={light('code')} />}
            onClick={showDevTools}
          >
            <Text>{t`Dev tools`}</Text>
          </MenuItem>
        )}

        <MenuItem icon={<PowerOffIcon color="red.500" />} onClick={signOutNow}>
          <Text color="red.500">{t`Sign out`}</Text>
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

export default AccountMenu;

declare global {
  interface Window {
    preload_AllowAccountSwitching?: boolean;
  }
}
